import { useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface Prediction {
  place_id: string;
  description: string;
  source: "db" | "google";
}

export function useGooglePlaces() {
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const searchDB = useCallback(async (input: string): Promise<Prediction[]> => {
    try {
      const { data: hotels } = await supabase
        .from("hotels")
        .select("id, name")
        .ilike("name", `%${input}%`)
        .limit(5);

      const { data: airports } = await supabase
        .from("airports")
        .select("id, name, code")
        .ilike("name", `%${input}%`)
        .limit(3);

      const results: Prediction[] = [];

      if (airports) {
        airports.forEach((a) => {
          results.push({
            place_id: `db-airport-${a.id}`,
            description: `✈️ ${a.name} (${a.code})`,
            source: "db",
          });
        });
      }

      if (hotels) {
        const uniqueHotels = new Map<string, typeof hotels[0]>();
        hotels.forEach((h) => {
          const key = h.name.toLowerCase();
          if (!uniqueHotels.has(key)) uniqueHotels.set(key, h);
        });
        uniqueHotels.forEach((h) => {
          results.push({
            place_id: `db-hotel-${h.id}`,
            description: `🏨 ${h.name}`,
            source: "db",
          });
        });
      }

      return results;
    } catch (err) {
      console.error("DB search error:", err);
      return [];
    }
  }, []);

  const search = useCallback((input: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!input || input.length < 2) {
      setPredictions([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const dbResults = await searchDB(input);
        setPredictions(dbResults);
      } catch (err) {
        console.error("Search error:", err);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, [searchDB]);

  const cleanLocationName = useCallback((name: string) => {
    return name.replace(/^[✈️🏨🏠🏖️🌴]+\s*/u, '').trim();
  }, []);

  const getDistanceMatrix = useCallback(async (from: string, to: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("google-maps", {
        body: { action: "distance-matrix", origins: cleanLocationName(from), destinations: cleanLocationName(to) },
      });
      if (!error && data && !data.error) {
        return {
          distanceKm: Math.round(data.distanceKm * 10) / 10,
          durationMin: Math.round(data.durationMin),
        };
      }
      console.error("Distance matrix error:", error || data?.error);
    } catch (err) {
      console.error("Distance matrix error:", err);
    }
    return null;
  }, [cleanLocationName]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return { predictions, loading, search, getDistanceMatrix, setPredictions };
}
